/**
 * WP-V2-PL — katalog obiektów Basenu (PLAN-V2 §6 Pool).
 * Czyste def + logika stanów (testowalna bez Phaser); scena konsumuje ObjectLayer.
 */
import type { ObjectDef, SnapZone, SpawnPlacement } from '../../interact/types.js';
import { MIN_OBJECT_SIZE } from '../../interact/types.js';
import type { ColliderRect } from '../../core/world/index.js';

/** Świat wnętrza: 1600×720 (PLAN-V2 §4.2). */
export const POOL_WORLD_W = 1600;
export const POOL_WORLD_H = 720;

/** Domyślny strój z kabiny (gdy gracz nie ma suit w OutfitStore). */
export const POOL_DEFAULT_SUIT_ID = 'suit_pink';

/** Etykiety PL (diakrytyki). */
export const POOL_LABELS_PL: Record<string, string> = {
  pool_cabin: 'Przebieralnia',
  pool_lounger_a: 'Leżak',
  pool_lounger_b: 'Leżak',
  pool_ball: 'Piłka plażowa',
  pool_ring: 'Koło ratunkowe',
  pool_shower: 'Prysznic',
  pool_trampoline: 'Trampolina',
  pool_splash: 'Brodzik',
  pool_towel: 'Ręcznik',
  pool_umbrella: 'Parasol',
};

/**
 * Legacy map (statusy / testy) — wizualnie obiekty używają proceduralnych tekstur
 * `obj_pool_*` (patrz PoolScene.ensurePoolTextures), nie emoji.
 */
export const POOL_ICONS: Record<string, string> = {
  pool_cabin: '🚪',
  pool_lounger_a: '🏖️',
  pool_lounger_b: '🏖️',
  pool_ball: '🏐',
  pool_ring: '🛟',
  pool_shower: '🚿',
  pool_trampoline: '🤸',
  pool_splash: '💦',
  pool_towel: '🧺',
  pool_umbrella: '⛱️',
};

/** Rodzaje proceduralnych rysunków basenu (gdy brak PNG). */
export type PoolDecorKind =
  | 'cabin'
  | 'lounger'
  | 'ball'
  | 'ring'
  | 'shower'
  | 'trampoline'
  | 'splash'
  | 'towel'
  | 'umbrella';

/** Mapa texture key → rodzaj rysunku (bez napisów / emoji). */
export const POOL_TEXTURE_SHAPES: Readonly<Record<string, PoolDecorKind>> = {
  obj_pool_cabin: 'cabin',
  obj_pool_lounger: 'lounger',
  obj_pool_ball: 'ball',
  obj_pool_ring: 'ring',
  obj_pool_shower: 'shower',
  obj_pool_trampoline: 'trampoline',
  obj_pool_splash: 'splash',
  obj_pool_towel: 'towel',
  obj_pool_umbrella: 'umbrella',
};

export function poolShapeForTexture(textureKey: string): PoolDecorKind | null {
  return POOL_TEXTURE_SHAPES[textureKey] ?? null;
}

export function poolTextureKeys(): string[] {
  return Object.keys(POOL_TEXTURE_SHAPES);
}

export type CurtainState = 'closed' | 'open';
export type WearState = 'off' | 'on';

/** Katalog ObjectDef — size ≥ 64; ≥8 interaktywnych. */
export function poolObjectDefs(): ObjectDef[] {
  const size = Math.max(MIN_OBJECT_SIZE, 72);
  return [
    {
      id: 'pool_cabin',
      texture: 'obj_pool_cabin',
      size: Math.max(MIN_OBJECT_SIZE, 96),
      interactions: ['toggle'],
      states: ['closed', 'open'],
      fx: 'sparkle',
      dogReaction: 'happy',
    },
    {
      id: 'pool_lounger_a',
      texture: 'obj_pool_lounger',
      size: Math.max(MIN_OBJECT_SIZE, 88),
      interactions: ['tap'],
      sound: 'sit',
      fx: 'hearts',
      dogReaction: 'happy',
    },
    {
      id: 'pool_lounger_b',
      texture: 'obj_pool_lounger',
      size: Math.max(MIN_OBJECT_SIZE, 88),
      interactions: ['tap'],
      sound: 'sit',
      fx: 'hearts',
      dogReaction: 'happy',
    },
    {
      id: 'pool_ball',
      texture: 'obj_pool_ball',
      size,
      interactions: ['bounce', 'drag'],
      fx: 'stars',
      dogReaction: 'jump',
    },
    {
      id: 'pool_ring',
      texture: 'obj_pool_ring',
      size: Math.max(MIN_OBJECT_SIZE, 80),
      interactions: ['tap', 'pickup'],
      states: ['off', 'on'],
      fx: 'splash',
      dogReaction: 'happy',
    },
    {
      id: 'pool_shower',
      texture: 'obj_pool_shower',
      size: Math.max(MIN_OBJECT_SIZE, 80),
      interactions: ['tap'],
      fx: 'splash',
      dogReaction: 'happy',
    },
    {
      id: 'pool_trampoline',
      texture: 'obj_pool_trampoline',
      size: Math.max(MIN_OBJECT_SIZE, 100),
      interactions: ['tap'],
      fx: 'splash',
      dogReaction: 'jump',
    },
    {
      id: 'pool_splash',
      texture: 'obj_pool_splash',
      size: Math.max(MIN_OBJECT_SIZE, 100),
      interactions: ['tap'],
      fx: 'splash',
      dogReaction: 'happy',
    },
    {
      id: 'pool_towel',
      texture: 'obj_pool_towel',
      size,
      interactions: ['pickup', 'drag'],
      fx: 'sparkle',
      dogReaction: 'sniff',
      snapTargets: ['pool_lounger'],
    },
    {
      id: 'pool_umbrella',
      texture: 'obj_pool_umbrella',
      size: Math.max(MIN_OBJECT_SIZE, 84),
      interactions: ['toggle'],
      states: ['open', 'closed'],
      fx: 'stars',
      dogReaction: 'sniff',
    },
  ];
}

/** Rozmieszczenie w basenie 1600×720. */
export function poolPlacements(): SpawnPlacement[] {
  return [
    { defId: 'pool_cabin', x: 160, y: 280 },
    { defId: 'pool_shower', x: 300, y: 260 },
    { defId: 'pool_umbrella', x: 420, y: 300 },
    { defId: 'pool_lounger_a', x: 380, y: 480 },
    { defId: 'pool_lounger_b', x: 520, y: 520 },
    { defId: 'pool_towel', x: 460, y: 440 },
    { defId: 'pool_splash', x: 320, y: 600 },
    { defId: 'pool_ring', x: 220, y: 560 },
    { defId: 'pool_ball', x: 700, y: 560 },
    { defId: 'pool_trampoline', x: 1180, y: 300 },
  ];
}

export function poolSnapZones(): SnapZone[] {
  return [
    { id: 'pool_lounger', x: 380, y: 480, radius: 64 },
    { id: 'pool_deck', x: 700, y: 560, radius: 72 },
  ];
}

/** Lekkie blokady: ściana + krawędź głębokiego basenu. */
export function poolColliders(): ColliderRect[] {
  return [
    { x: 0, y: 0, width: POOL_WORLD_W, height: 100 },
    // tafla głębokiego basenu (środek-prawo) — nie da się wejść „na sucho” w środek tafli
    { x: 760, y: 340, width: 520, height: 40 },
  ];
}

/** Lista oczekiwanych assetów PNG (placeholdery do czasu artu). */
export function poolMissingAssetKeys(): string[] {
  const keys = new Set<string>();
  for (const d of poolObjectDefs()) {
    keys.add(d.texture);
    if (d.states) {
      for (const s of d.states) {
        keys.add(`${d.texture}_${s}`);
        keys.add(`obj_${d.id}_${s}`);
      }
    }
  }
  return [...keys].sort();
}

// ─── Czysta logika (E2E / unit) ─────────────────────────────────────────────

/** Zasuń / odsuń kotarę kabiny. */
export function toggleCurtain(current: CurtainState): CurtainState {
  return current === 'closed' ? 'open' : 'closed';
}

/**
 * Po użyciu kabiny: odsuń kotarę (jeśli zamknięta) i załóż strój kąpielowy.
 * Zwraca suit do OutfitStore + tryb swim.
 */
export function cabinChangeSwimwear(
  curtain: CurtainState,
  currentSuit: string | null | undefined,
): {
  curtain: CurtainState;
  suitId: string;
  mode: 'swim';
  changed: boolean;
} {
  const nextCurtain = curtain === 'closed' ? 'open' : curtain;
  const suitId =
    typeof currentSuit === 'string' && currentSuit.trim().length > 0
      ? currentSuit
      : POOL_DEFAULT_SUIT_ID;
  return {
    curtain: nextCurtain,
    suitId,
    mode: 'swim',
    changed: true,
  };
}

/** Czy brodzik / plusk dozwolony (strój lub koło). */
export function canSplashInPad(opts: {
  hasSuit: boolean;
  wearingRing: boolean;
}): boolean {
  return opts.hasSuit || opts.wearingRing;
}

/** Reakcja brodzika — plusk + swim gdy w stroju. */
export function splashPadAction(opts: {
  hasSuit: boolean;
  wearingRing: boolean;
}): { ok: boolean; action: 'swim' | null; fx: 'splash' | null; needSuit: boolean } {
  if (!canSplashInPad(opts)) {
    return { ok: false, action: null, fx: null, needSuit: true };
  }
  return {
    ok: true,
    action: opts.hasSuit ? 'swim' : 'swim',
    fx: 'splash',
    needSuit: false,
  };
}

/** Trampolina: skok + plusk (zawsze skok; plusk gdy ma strój). */
export function trampolineAction(hasSuit: boolean): {
  action: 'jump';
  splash: boolean;
} {
  return { action: 'jump', splash: true /* plusk w wodę zawsze wizualny */ };
}

/** Leżak → pies się kładzie (sleep). */
export function loungerAction(defId: string): 'sleep' | null {
  return defId === 'pool_lounger_a' || defId === 'pool_lounger_b' ? 'sleep' : null;
}

/** Załóż / zdejmij koło ratunkowe. */
export function toggleRing(wearing: WearState): WearState {
  return wearing === 'off' ? 'on' : 'off';
}

/** Pływanie w brodziku z kołem. */
export function ringFloatAction(wearing: WearState): {
  floating: boolean;
  action: 'swim' | null;
} {
  if (wearing !== 'on') return { floating: false, action: null };
  return { floating: true, action: 'swim' };
}

/** Prysznic: strumień + otrzepanie (happy). */
export function showerAction(defId: string): 'happy' | null {
  return defId === 'pool_shower' ? 'happy' : null;
}

/** Parasol open/closed. */
export function toggleUmbrella(current: 'open' | 'closed'): 'open' | 'closed' {
  return current === 'open' ? 'closed' : 'open';
}

export function isPoolBall(defId: string): boolean {
  return defId === 'pool_ball';
}

export function isTrampoline(defId: string): boolean {
  return defId === 'pool_trampoline';
}

export function isSplashPad(defId: string): boolean {
  return defId === 'pool_splash';
}

export function isCabin(defId: string): boolean {
  return defId === 'pool_cabin';
}

export function isRing(defId: string): boolean {
  return defId === 'pool_ring';
}

export function isUmbrella(defId: string): boolean {
  return defId === 'pool_umbrella';
}

/** ≥8 typów interaktywnych (DoD §6). */
export function countInteractiveKinds(defs: ObjectDef[] = poolObjectDefs()): number {
  return defs.filter((d) => d.interactions.length > 0).length;
}

export function allDefsMeetMinTouch(defs: ObjectDef[] = poolObjectDefs()): boolean {
  return defs.every((d) => d.size >= MIN_OBJECT_SIZE);
}

/**
 * E2E sekwencja: kabina → strój → brodzik plusk → trampolina skok.
 * Czysta maszyna stanów pod testy bez Phaser.
 */
export type PoolE2EState = {
  curtain: CurtainState;
  suitId: string | null;
  mode: 'normal' | 'swim';
  splashed: boolean;
  trampolineJumped: boolean;
};

export function poolE2EInitial(): PoolE2EState {
  return {
    curtain: 'closed',
    suitId: null,
    mode: 'normal',
    splashed: false,
    trampolineJumped: false,
  };
}

export function poolE2EStepCabin(state: PoolE2EState): PoolE2EState {
  const r = cabinChangeSwimwear(state.curtain, state.suitId);
  return {
    ...state,
    curtain: r.curtain,
    suitId: r.suitId,
    mode: r.mode,
  };
}

export function poolE2EStepSplash(state: PoolE2EState): PoolE2EState {
  const hasSuit = typeof state.suitId === 'string' && state.suitId.length > 0;
  const r = splashPadAction({ hasSuit, wearingRing: false });
  if (!r.ok) return state;
  return { ...state, splashed: true };
}

export function poolE2EStepTrampoline(state: PoolE2EState): PoolE2EState {
  const t = trampolineAction(state.mode === 'swim');
  return { ...state, trampolineJumped: t.action === 'jump' };
}

export function poolE2EComplete(state: PoolE2EState): boolean {
  return (
    state.mode === 'swim' &&
    !!state.suitId &&
    state.curtain === 'open' &&
    state.splashed &&
    state.trampolineJumped
  );
}
